import React, { useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useFetcher } from 'react-router-dom'

import Button from '@components/shared/Button'
import Dialog from '@components/shared/Dialog'
import Checkbox from '@components/shared/form/Checkbox'
import Input from '@components/shared/form/Input'
import { useDataObject } from '@hooks/useDataObject'
import { useDialogStore } from '@stores/dialog.store'
import { useSession } from '@stores/globalData/globalData.store'

import type Collection from '@models/Collection'
import type { CollectionResponse } from '@pages/home/actions/collection/addCollection.action'

type DialogState = {
  collection?: Collection
}

interface Props {
  standalone?: boolean
}
const CollectionFormDialog: React.FC<Props> = (props) => {
  const { t } = useTranslation()
  const state = useDialogStore<DialogState>((s) => s.state)
  const activeDialogs = useDialogStore((s) => s.activeDialogs)

  const isEdit = activeDialogs.includes('add_collection') && state?.collection

  return (
    <Dialog
      name="add_collection"
      title={t(isEdit ? 'action.editDetails' : 'content.newCollection')}
      maxWidth={364}>
      <AddCollectionBody {...props} />
    </Dialog>
  )
}
export default CollectionFormDialog

type FormType = {
  name?: string
  description?: string | null
  publicCollection?: boolean
  isMarketplaceCollection?: boolean
  buttonLabel?: string | null
  query?: string | null
}

const AddCollectionBody: React.FC<Props> = ({ standalone }) => {
  const { t } = useTranslation()
  const openDialog = useDialogStore((state) => state.openDialog)
  const closeDialog = useDialogStore((state) => state.closeDialog)
  const state = useDialogStore<DialogState>((state) => state.state)
  const session = useSession()

  const fetcher = useFetcher<CollectionResponse>()
  const [showSearch, setShowSearch] = useState(false)

  const collection = state?.collection
  const initialData = useRef(getInitialData(collection))

  const [data, updateData] = useDataObject<FormType>(initialData.current)

  useEffect(() => {
    if (!fetcher.data || ['submitting', 'loading'].includes(fetcher.state)) return

    const { collection } = fetcher.data
    if (collection) {
      if (standalone) closeDialog('add_collection')
      else openDialog('add_to_collection', { closeOthers: true })
    }
  }, [fetcher.data, fetcher.state])

  return (
    <>
      {!collection && <p className="mb-4 text-md">{t('messages.createCollection')}</p>}
      <fetcher.Form
        method="post"
        action={collection ? '/home/updateCollection' : '/home/addCollection'}>
        {collection && <input type="hidden" name="collection_id" value={collection.id} />}
        <Input
          name="name"
          type="text"
          label={t('content.title')}
          required
          value={data.name ?? ''}
          placeholder={t('placeholder.newCollection')}
          onChange={(e) => updateData({ name: e.target.value })}
        />
        <Input
          name="description"
          type="text"
          className="mt-1"
          label={t('content.description')}
          value={data.description ?? ''}
          placeholder={t('placeholder.newCollectionDescription')}
          onChange={(e) => updateData({ description: e.target.value })}
          multiline
          rows={3}
        />

        {session.gatedFeatures.showPublicCollections() && (
          <Checkbox
            name="public"
            label={t('content.shareThisCollection')}
            checked={data.publicCollection}
            onChecked={(val) => {
              updateData({ publicCollection: val })
            }}
          />
        )}

        {session.isNoteflightAdmin() && (
          <>
            <Checkbox
              name="is_marketplace_collection"
              className="mt-2"
              label={t('content.isMarketplaceCollection')}
              checked={data.isMarketplaceCollection}
              onChecked={(val) => {
                updateData({ isMarketplaceCollection: val })
              }}
            />
            <Checkbox
              name="showSearch"
              className="mt-2"
              label={t('content.linkToFiltered')}
              checked={showSearch}
              onChecked={(checked) => {
                setShowSearch(checked)
                if (!checked) updateData({ query: '', buttonLabel: '' })
                else updateData({ buttonLabel: 'content.viewMore' })
              }}
            />
            {showSearch && (
              <>
                <Input
                  name="query"
                  type="text"
                  className="mt-2"
                  placeholder={t('placeholder.pasteSearchUrl')}
                  onChange={(e) => updateData({ query: e.target.value })}
                  value={data.query ?? ''}
                />
                <Input
                  name="button_label"
                  className="mt-1"
                  type="text"
                  label={`${t('content.buttonLabel')} (${t(data.buttonLabel ?? '--')})`}
                  onChange={(e) => updateData({ buttonLabel: e.target.value })}
                  value={data.buttonLabel ?? ''}
                />
              </>
            )}
          </>
        )}

        <div className="flex flex-row justify-end w-full mt-4">
          <Button
            variant="secondary"
            className="mr-2"
            onClick={() => openDialog('add_to_collection', { closeOthers: true })}>
            {t('action.cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={!data.name || ['submitting', 'loading'].includes(fetcher.state)}>
            {t('action.save')}
          </Button>
        </div>
      </fetcher.Form>
    </>
  )
}

function getInitialData(collection?: Collection): FormType {
  if (!collection) {
    return { publicCollection: false, isMarketplaceCollection: false }
  }
  return {
    name: collection.name,
    buttonLabel: collection.button_label,
    description: collection.description,
    isMarketplaceCollection: collection.is_marketplace_collection,
    query: collection.query,
    publicCollection: collection.public,
  }
}
